import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'

import { Subtitle1, Subtitle2 } from '@src/elements/typography/typography'
import Member from '@src/data/member'
import { TimeSwitcher } from '@src/elements/time-switch/time-switch'
import { getCurrentMonthPeriods } from '@src/util/functions'
import { Spacer } from '@src/elements/spacer/spacer'
import { MemberService } from '@src/service/member'
import { MemberProfile } from '@src/data/member-profile'
import { convertMonth, convertWeekDay } from '@src/util/date-format'
import { getAreaSplineChartOptions } from '@src/util/area-chart-options'
import { DateFilterType } from '@src/elements/time-switch/date-filter-type'
import VueApexCharts from 'vue-apexcharts'

Vue.use(VueApexCharts)

Vue.component('apexchart', VueApexCharts)

import './training-frequency.sass'
import { getChartRef } from '@src/components/chart/utils'

export type TrainingFrequencyResponse = {
  weekly_training_frequency: {
    friday: number
    monday: number
    saturday: number
    sunday: number
    thursday: number
    tuesday: number
    wednesday: number
  } | null,
  yearly_training_frequency: {
    january: number
    february: number
    march: number
    april: number
    may: number
    june: number
    july: number
    august: number
    september: number
    october: number
    november: number
    december: number
  } | null
}

@Component({
  computed: {
    locale () {
      return this.$store.state.i18n.locale
    }
  }
})
export class TrainingFrequency extends Vue {

  @Prop({ required: true })
  member: Member | MemberProfile

  private chartOptions: any = null
  private series: any = []
  private initialChartWidth: any = '100%'

  constructor () {
    super()
  }

  @Watch('locale')
  async updateTitleOnLanguageChange () {
    await this.loadActivity(DateFilterType.LAST_MONTHS, getCurrentMonthPeriods())
    this.$forceUpdate()
  }

  mounted () {
    window.addEventListener('beforeprint', this.beforePrint)
    window.addEventListener('afterprint', this.afterPrint)
  }

  beforeDestroy () {
    window.removeEventListener('beforeprint', this.beforePrint)
    window.removeEventListener('afterprint', this.afterPrint)
  }

  getChartConfig (data: any[]) {
    let maxValue: number = data.length > 0 ? data.reduce((prev, current) => (prev[1] > current[1]) ? prev : current)[1] : 0

    let dateLabels: any[] = []
    // aggregated values per day/month
    let values: any[] = []
    data.forEach(entry => {
      dateLabels.push(entry[0])
      values.push(entry[1])
    })
    return { maxValue, dateLabels, values }
  }

  changeTimeFrame (timeFrame: DateFilterType, periods: any[]) {
    return this.loadActivity(timeFrame, periods)
  }

  beforePrint () {
    const chart = getChartRef(this.$refs, 'training-frequency-chart')

    const printWidth = 650
    this.initialChartWidth = this.initialChartWidth || chart.opts.chart.width
    chart.updateOptions({
      chart: {
        width: printWidth,
        animations: {
          enabled: false
        }
      }
    }, true, true, true)
  }

  afterPrint () {
    const chart = getChartRef(this.$refs, 'training-frequency-chart')

    chart.updateOptions({
      chart: {
        width: this.initialChartWidth,
        animations: {
          enabled: false
        }
      }
    }, true, true, true)
  }

  render (h: CreateElement) {
    if (!this.chartOptions) return ''
    return <div class='training-frequency'>
      <div class='row'>
        <div class='column'>
          <Subtitle1>{this.$t('PAGE.MEMBERS.STATUS.TRAINING_FREQUENCY')}</Subtitle1>
          <Subtitle2>{this.$t('PAGE.MEMBERS.STATUS.TRAINING_FREQUENCY_DESCRIPTION')}</Subtitle2>
        </div>
        <TimeSwitcher dateFilters={[DateFilterType.LAST_MONTHS, DateFilterType.LAST_YEAR]} change={this.changeTimeFrame} align='right' />
      </div>
      <Spacer />
      <Spacer />

      <div id={'training-frequency-chart'}>
        <apexchart
          ref='training-frequency-chart'
          options={this.chartOptions}
          height={this.chartOptions.chart.height}
          series={this.series} />
      </div>
    </div>
  }

  beforeMount () {
    return this.loadActivity(DateFilterType.LAST_MONTHS, getCurrentMonthPeriods())
  }

  buildChartOptions (dateLabels: any, maxY: number) {
    const options = getAreaSplineChartOptions(dateLabels, null, maxY)
    return {
      ...options,
      tooltip: {
        ...options.tooltip,
        x: {
          formatter: function (value, { dataPointIndex }) {
            return dateLabels[dataPointIndex]
          }
        }
      }
    }
  }

  protected isDestroying () {
    return (this as any)._isDestroyed || (this as any)._isBeingDestroyed
  }

  protected loadActivity (timeFrame: DateFilterType, periods: any[]) {
    return new MemberService().getTrainingFrequency(this.member.id, timeFrame, periods)
      .then(activities => {
        if (this.isDestroying()) return

        const data = activities.map(activity => {
          let date = timeFrame === DateFilterType.LAST_YEAR ? convertMonth(activity[0], 'MMM') : convertWeekDay(activity[0], 'ddd')
          return [date, activity[1]]
        })
        const { dateLabels, values, maxValue } = this.getChartConfig(data)
        this.chartOptions = this.buildChartOptions(dateLabels, maxValue)
        this.series = [
          {
            id: 'series',
            data: values,
            color: '#a7cff3',
            name: this.$t('PAGE.MEMBERS.STATUS.TRAINING_FREQUENCY')
          }
        ]

      })
      .finally(() => {
        if (!this.isDestroying()) this.$forceUpdate()
      })
  }
}
